import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from "jwt-decode";
import { switchRole as switchRoleApi } from '../api/authApi';
import NavDropdown from './NavDropdown';
import { clearSession } from '../utils/auth';

const getStoredToken = () => localStorage.getItem('accessToken');

const HomePage = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(getStoredToken);
  const [decoded, setDecoded] = useState(() => {
    const storedToken = getStoredToken();
    if (!storedToken) {
      return null;
    }
    try {
      return jwtDecode(storedToken);
    } catch (error) {
      console.error('Failed to decode stored token:', error);
      return null;
    }
  });
  const [isSwitching, setIsSwitching] = useState(false);
  const [switchError, setSwitchError] = useState('');

  useEffect(() => {
    if (!token) {
      setDecoded(null);
      return;
    }

    try {
      const nextDecoded = jwtDecode(token);
      setDecoded(nextDecoded);
    } catch (error) {
      console.error('Failed to decode token:', error);
      setDecoded(null);
    }
  }, [token]);

  const handleLogout = useCallback(() => {
    clearSession();
    setToken(null);
    setDecoded(null);
    navigate('/', { replace: true });
  }, [navigate]);

  const handleSwitchRole = useCallback(async (targetRole) => {
    if (!token) {
      return;
    }

    setIsSwitching(true);
    setSwitchError('');

    try {
      const response = await switchRoleApi(targetRole, token);
      const { status, message, data } = response.data || {};

      if (status && data?.token) {
        localStorage.setItem('accessToken', data.token);
        if (data.refreshToken) {
          localStorage.setItem('refreshToken', data.refreshToken);
        }
        if (data.role) {
          localStorage.setItem('role', data.role || targetRole);
        }
        setToken(data.token);
        setSwitchError('');
        navigate('/Dashboard', { replace: true });
      } else {
        setSwitchError(message || 'Failed to switch role.');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to switch role.';
      setSwitchError(errorMessage);
    } finally {
      setIsSwitching(false);
    }
  }, [navigate, token]);
  
  const loginType = localStorage.getItem("loginType") || '';
  const role = decoded?.role || '';
  const roleLabel = role ? role.charAt(0).toUpperCase() + role.slice(1) : 'Unknown';

  const canSwitchToTrainer = loginType === 'admin' && role === 'admin';
  const canSwitchToAdmin = loginType === 'admin' && role === 'trainer';

  return (
    <div style={{ position: 'relative', textAlign: 'center', marginTop: '50px' }}>
      <div style={{ position: 'absolute', top: '20px', right: '20px', textAlign: 'right' }}>
        {switchError && (
          <div style={{ color: 'red', marginBottom: '8px', fontSize: '12px' }}>
            {switchError}
          </div>
        )}
        {canSwitchToTrainer && (
          <button
            style={{ padding: '8px 16px',border:'1px solid rgba(0, 0, 0, 0.8)', borderRadius: '8px', cursor: 'pointer' }}
            onClick={() => handleSwitchRole('trainer')}
            disabled={isSwitching}
          >
            {isSwitching ? 'Switching…' : 'Switch to Trainer'}
          </button>
        )}
        {canSwitchToAdmin && (
          <button
            style={{ padding: '8px 16px', border:'1px solid rgba(0, 0, 0, 0.8)', borderRadius: '8px', cursor: 'pointer', marginLeft: canSwitchToTrainer ? '8px' : '0' }}
            onClick={() => handleSwitchRole('admin')}
            disabled={isSwitching}
          >
            {isSwitching ? 'Switching…' : 'Switch to Admin'}
          </button>
        )}
      </div>
      <h1>Chess Tournament Manager</h1>
      <div style={{
        display: 'block',
        margin: '20px 0',
        padding: '8px 20px',
        backgroundColor: 'rgba(255, 255, 255)',
        transition: 'all 0.3s ease'
      }}>
        <span style={{
          fontSize: '12px',
          fontWeight: '400',
          textTransform: 'uppercase',
          letterSpacing: '1.5px',
          marginRight: '10px'
        }}>
          Role
        </span>
        <span style={{
          fontSize: '14px',
          fontWeight: '600',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          color: '#fff',
          padding: '3px 10px',
          borderRadius: '12px',
          textTransform: 'capitalize',
          letterSpacing: '0.5px'
        }}>
          {roleLabel}
        </span>
      </div>

      {/* Super Admin - can manage admins, teams, and tournaments */}
      {role === 'superadmin' && (
        <>
          <button
            style={{ margin: '10px', padding: '10px 20px', cursor: 'pointer' }}
            onClick={() => navigate('/create-admin')}
          >
            Admin Management
          </button>
          <button
            style={{ margin: '10px', padding: '10px 20px', cursor: 'pointer' }}
            onClick={() => navigate('/create-team')}
          >
            Manage Group
          </button>
          <button
            style={{ margin: '10px', padding: '10px 20px', cursor: 'pointer' }}
            onClick={() => navigate('/team-manage')}
          >
            Manage Users
          </button>
          <NavDropdown
            label="Tournament"
            items={[
              { label: 'Create Tournament', onClick: () => navigate('/create-tournament') },
              { label: 'Manage Tournaments', onClick: () => navigate('/fetch-tournaments') }
            ]}
          />
          <NavDropdown
            label="Subscription"
            items={[
              { label: 'Create Subscription', onClick: () => navigate('/create-subscription') },
              { label: 'Manage Subscription', onClick: () => navigate('/manage-subscription') }
            ]}
          />
          <NavDropdown
            label="Blog"
            items={[
              { label: 'Create Blog', onClick: () => navigate('/create-blog') },
              { label: 'Manage Blogs', onClick: () => navigate('/manage-blogs') }
            ]}
          />
          <NavDropdown
            label="Policy"
            items={[
              { label: 'Create Policy', onClick: () => navigate('/create-policy') },
              { label: 'Manage Policy', onClick: () => navigate('/manage-policy') }
            ]}
          />
          <button
            style={{ margin: '10px', padding: '10px 20px', backgroundColor: 'red', color: 'white', cursor: 'pointer', borderRadius: '10px' }}
            onClick={handleLogout}
          >
            Logout
          </button>
        </>
      )}

      {/* Admin - can only manage trainers and view tournaments/users */}
      {role === 'admin' && (
        <>
          <button
            style={{ margin: '10px', padding: '10px 20px', cursor: 'pointer' }}
            onClick={() => navigate('/create-trainer')}
          >
            Manage Trainers
          </button>
          <button
            style={{ margin: '10px', padding: '10px 20px', cursor: 'pointer' }}
            onClick={() => navigate('/admin-view-tournaments')}
          >
            View Tournaments
          </button>
          <button
            style={{ margin: '10px', padding: '10px 20px', cursor: 'pointer' }}
            onClick={() => navigate('/admin-view-users')}
          >
            View Members
          </button>         
           <button
            style={{ margin: '10px', padding: '10px 20px', cursor: 'pointer' }}
            onClick={() => navigate('/change-password')}
          >
            Change Password
          </button>
           <button
            style={{ margin: '10px', padding: '10px 20px', backgroundColor: 'red', color: 'white', borderRadius: '10px', cursor: 'pointer' }}
            onClick={handleLogout}
          >
            Logout
          </button>
          <p style={{ color: '#888', marginTop: '20px' }}>
            Note: To manage teams and tournaments, please create trainers. You can view tournaments and users created by your trainers.
          </p>
        </>
      )}

      {/* Trainer - can manage teams and tournaments */}
      {role === 'trainer' && (
        <>
          <button
            style={{ margin: '10px', padding: '10px 20px', cursor: 'pointer' }}
            onClick={() => navigate('/trainer-profile')}
          >
            My Profile
          </button>
          <button
            style={{ margin: '10px', padding: '10px 20px', cursor: 'pointer' }}
            onClick={() => navigate('/video-lessons')}
          >
            My Lessons
          </button>
          <button
            style={{ margin: '10px', padding: '10px 20px', cursor: 'pointer' }}
            onClick={() => navigate('/create-team')}
          >
            Manage Groups
          </button>
          <button
            style={{ margin: '10px', padding: '10px 20px', cursor: 'pointer' }}
            onClick={() => navigate('/team-manage')}
          >
            Manage Users
          </button>
          <NavDropdown
            label="Tournament"
            items={[
              { label: 'Create Tournament', onClick: () => navigate('/create-tournament') },
              { label: 'Manage Tournaments', onClick: () => navigate('/fetch-tournaments') }
            ]}
          />          
           <button
            style={{ margin: '10px', padding: '10px 20px', cursor: 'pointer' }}
            onClick={() => navigate('/change-password')}
          >
            Change Password
          </button>
          <button
            style={{ margin: '10px', padding: '10px 20px', backgroundColor: 'red', color: 'white', borderRadius: '10px', cursor: 'pointer' }}
            onClick={handleLogout}
          >
            Logout
          </button>
        </>
      )}

      <div style={{ margin: '0', padding: '0', height: '75vh', width: '100%', overflow: 'hidden' }}>
        <img
          src='https://images.unsplash.com/photo-1523875194681-bedd468c58bf?fm=jpg&q=60&w=3000&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTF8fGNoZXNzfGVufDB8fDB8fHww'
          alt="Chess"
          style={{
            height: '100%',
            width: '100%',
            objectFit: 'cover',
            display: 'block'
          }}
        />
      </div>
    </div>
  );
};

export default HomePage;
